import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { CrownIcon, CalendarIcon, GiftIcon, UsersIcon } from 'lucide-react';
const benefits = [{
  icon: CalendarIcon,
  title: 'Priority Booking',
  description: 'Access to exclusive time slots and advance scheduling privileges.'
}, {
  icon: GiftIcon,
  title: 'Special Events',
  description: 'Invitations to private workshops, demonstrations, and member gatherings.'
}, {
  icon: UsersIcon,
  title: 'Community Access',
  description: 'Connect with like-minded individuals in a vetted, private community.'
}, {
  icon: CrownIcon,
  title: 'Personalized Experiences',
  description: 'Customized session planning and ongoing mentorship opportunities.'
}];
export function Members() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section id="members" ref={ref} className="py-24 lg:py-32 bg-aubergine-deep relative overflow-hidden" data-id="element-207">
      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gold/5 rounded-full blur-3xl" data-id="element-208"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-gold/5 rounded-full blur-3xl" data-id="element-209"></div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10" data-id="element-210">
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-211">
          <p className="text-gold text-xs uppercase tracking-[0.3em] mb-4 font-cinzel" data-id="element-212">
            Private Members Area
          </p>
          <h2 className="font-cormorant text-bone text-4xl lg:text-6xl font-light mb-6" data-id="element-213">
            Enter the Inner Circle
          </h2>
          <p className="text-silver-muted text-lg max-w-2xl mx-auto" data-id="element-214">
            An exclusive community for dedicated practitioners seeking deeper
            engagement and ongoing connection.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center mb-12" data-id="element-215">
          {/* Portrait */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 1,
          delay: 0.2
        }} className="lg:col-span-5 relative" data-id="element-216">
            <div className="relative aspect-[3/4] w-full max-w-md mx-auto" data-id="element-217">
              <div className="absolute -inset-4 border border-gold/30" data-id="element-218"></div>
              <div className="absolute -top-4 -left-4 w-14 h-14 border-t border-l border-gold/60" data-id="element-219"></div>
              <div className="absolute -bottom-4 -right-4 w-14 h-14 border-b border-r border-gold/60" data-id="element-220"></div>
              <img src="https://cdn.magicpatterns.com/uploads/kdcQby3WJfUBgffbjKrjmM/14.jpg" alt="Madam Yas — Inner Circle" className="relative w-full h-full object-cover object-top" data-id="element-221" />
              <div className="absolute inset-0 bg-gradient-to-t from-aubergine-deep/40 via-transparent to-transparent pointer-events-none" data-id="element-222"></div>
            </div>
          </motion.div>

          {/* Benefits Grid */}
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-6" data-id="element-223">
            {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return <motion.div key={benefit.title} initial={{
              opacity: 0,
              y: 30
            }} animate={isInView ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.6,
              delay: 0.3 + index * 0.1
            }} className="flex gap-5 bg-onyx/50 border border-gold/10 p-6" data-id="element-224">
                  <div className="flex-shrink-0" data-id="element-225">
                    <div className="w-12 h-12 border border-gold/30 flex items-center justify-center" data-id="element-226">
                      <Icon size={22} className="text-gold" data-id="element-227" />
                    </div>
                  </div>
                  <div data-id="element-228">
                    <h3 className="font-cormorant text-bone text-xl mb-2" data-id="element-229">
                      {benefit.title}
                    </h3>
                    <p className="text-silver-muted text-sm leading-relaxed" data-id="element-230">
                      {benefit.description}
                    </p>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>

        <motion.div initial={{
        opacity: 0
      }} animate={isInView ? {
        opacity: 1
      } : {}} transition={{
        duration: 0.8,
        delay: 0.5
      }} className="text-center" data-id="element-231">
          <p className="text-silver-muted mb-6 max-w-2xl mx-auto" data-id="element-232">
            Membership is by invitation only and requires a minimum of three
            successful sessions. If you are interested in joining, please
            express your interest during your consultation.
          </p>
          <a href="#contact" className="inline-block px-8 py-4 bg-gold text-ink text-sm uppercase tracking-widest font-medium hover:bg-bone transition-all duration-300" data-id="element-233">
            Apply for Membership
          </a>
        </motion.div>
      </div>
    </section>;
}